import { useBoardValue } from "frontend/state/board-state";
import StyledButton from "frontend/ui-components/styled-button";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useBoardIntegrations } from "frontend/hooks/use-board-integrations";
import { getIntegrationConfigData, getIntegrationItems } from "frontend/api";
import { useDebounce } from "frontend/hooks/debounce";
import classNames from "classnames";
import { IntegrationType, MondayIntegrationType } from "shared/integrations/integration";
import { placeMindMapOrgChart } from "shared/datamodel/mindmap-org-chart";
import { Arrow } from "frontend/ui-components/svg-shapes";
import * as R from "rambda";
import style from "./template-org-chart-import-modal.module.css";
import { getPathPrefix } from "../utils/getPathPrefix";
import DropdownPicker from "frontend/ui-components/picker/dropdown-picker";
import { LiveIntegrationColumn } from "frontend/canvas-designer-new/elements-toolbar/widgets/live-integration/utils";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { SourceNodeType } from "frontend/canvas-designer-new/elements/org-chart/orgchart-utils";
import { CanceledError } from "axios";

enum OrgChartImportSteps {
  LoadingData,
  ChooseBoard,
  MapColumns,
  PickRoot,
}

interface DepartmentOption {
  id: string;
  columnId: string;
  title: string;
}

interface MandatoryColumnsMapping {
  selfId: string | null;
  parentId: string | null;
  jobTitle: string | null;
  departmentId?: string | null;
}

const ColumnForName = /(Employee )?Name/i;
const ColumnForDirectManager = /direct manager/i;
const ColumnForPosition = /position|role|job/i;
const ColumnForDepartment = /department/i;

// Minimum requirements for a board to become org chart:
// one column for employee name of type: text, email,people or name
// one column for direct manager of type: text or email
// one column for position of type: text
// columns of type 'people' should have at most a single entry in each row (direct manager can be empty, name cannot be)
const PossibleTypesForFields = {
  "Employee name": ["text", "email", "people", "name"],
  "Direct manager": ["text", "email", "people"],
  Position: ["text"],
  Department: ["status"],
};

const { Department, ...TypesForMandatoryFields } = PossibleTypesForFields;
const requiredColumns = Object.values(TypesForMandatoryFields);

function boardHasMinimumRequirements({ columns }: { columns: { type: string }[] }): boolean {
  if (columns.length < Object.keys(requiredColumns).length) {
    return false;
  }

  // This function looks for some combination of columns that will match the requirements
  // The requirements are at 'PossibleTypesForFields' (mapping of column name to possible types for that column)
  // The way to check a board is recursive:
  // take the first required column and try to find a match for it in the columns of the board (some column with the same type)
  // if found, remove that column from the list and continue to the next required column. If not found, return false
  // The recursion ends when all required columns are matched, or we have still unmet requirements and no more columns
  function findMatch(columns: { type: string }[], requiredIndex = 0): boolean {
    // Check if we matched all our requirements
    if (requiredIndex >= requiredColumns.length) {
      return true;
    }
    // Check if we have more requirements than columns left
    if (requiredColumns.length - requiredIndex > columns.length) {
      return false;
    }
    // Otherwise, try to find a match for the current required column
    // If found, remove that column from the list and continue to the next required column
    for (let i = 0; i < requiredColumns[requiredIndex].length; i++) {
      const j = columns.findIndex((col) => col.type == requiredColumns[requiredIndex][i]);
      if (j != -1) {
        const remainingColumns = columns.filter((_, index) => index != j);
        if (findMatch(remainingColumns, requiredIndex + 1)) {
          return true;
        }
      }
    }
    return false;
  }
  return findMatch(columns);
}

export default function TemplateOrgchartImportModal({ onDismiss }: { onDismiss?: (template?: any) => void }) {
  const shouldEnableDepartmentAsRoot = useFeatureFlag("orgchart-department-enabled");
  const shouldExcludeRestrictedColumns = useFeatureFlag("restricted-columns");

  const [{ board, documentId }, _] = useBoardValue();

  const { mondayIntegration, boardIntegrations, addBoardIntegration, updateBoardIntegration, isLoaded } =
    useBoardIntegrations(board?.documentId);

  const [selectedIntegration, setSelectedIntegration] = useState<any>(null);
  const [selectedBoardName, setSelectedBoardName] = useState<string>("");
  const [selectedColumnMapping, setSelectedColumnMapping] = useState<MandatoryColumnsMapping>({
    selfId: null,
    parentId: null,
    jobTitle: null,
    ...(shouldEnableDepartmentAsRoot ? { departmentId: null } : {}),
  });
  const [selectedSourceType, setSelectedSourceType] = useState<SourceNodeType>(SourceNodeType.Person);

  const [step, setStep] = useState(OrgChartImportSteps.LoadingData);
  const [columnDropdownOpen, setColumnDropdownOpen] = useState("");

  useEffect(() => {
    if (isLoaded && mondayIntegration) {
      setSelectedColumnMapping({
        selfId: null,
        parentId: null,
        jobTitle: null,
        ...(shouldEnableDepartmentAsRoot ? { departmentId: null } : {}),
      });
      setStep(OrgChartImportSteps.ChooseBoard);
    }
  }, [isLoaded, mondayIntegration]);

  useEffect(() => {
    if (selectedIntegration) {
      if (selectedIntegration.configuration && selectedIntegration.configuration.columnMapping) {
        setSelectedColumnMapping({ ...selectedIntegration.configuration.columnMapping });
      }
    }
  }, [selectedIntegration]);

  function renderBoardPicker() {
    async function chooseBoard(board: any) {
      if (!mondayIntegration) {
        return;
      }
      const { id: boardId, columns, name } = board;
      setSelectedBoardName(name);
      if (boardIntegrations) {
        const existing = boardIntegrations.find(
          (integration: any) =>
            integration.integration_id === mondayIntegration.id &&
            integration.configuration.boardId === boardId &&
            integration.configuration.type === MondayIntegrationType.org_chart
        );
        if (existing) {
          setSelectedIntegration(existing);
          setStep(OrgChartImportSteps.MapColumns);
          return;
        }
      }

      function findAutoMappedColumnId(columns: LiveIntegrationColumn[], supportedTypes: string[], columnRegex: RegExp) {
        const mappedColumn = columns.find((col) => supportedTypes.includes(col.type) && columnRegex.test(col.title));
        return mappedColumn?.id;
      }

      const config = {
        boardId: boardId,
        columns: columns,
        type: MondayIntegrationType.org_chart,
        columnMapping: {
          selfId: findAutoMappedColumnId(columns, PossibleTypesForFields["Employee name"], ColumnForName),
          parentId: findAutoMappedColumnId(columns, PossibleTypesForFields["Direct manager"], ColumnForDirectManager),
          jobTitle: findAutoMappedColumnId(columns, PossibleTypesForFields["Position"], ColumnForPosition),
          ...(shouldEnableDepartmentAsRoot
            ? {
                departmentId: findAutoMappedColumnId(
                  columns,
                  PossibleTypesForFields["Department"],
                  ColumnForDepartment
                ),
              }
            : {}),
        },
      };

      const createdId = await addBoardIntegration(mondayIntegration.id, config);
      setSelectedIntegration({ id: createdId, configuration: config });
      setStep(OrgChartImportSteps.MapColumns);
    }

    async function fetchItems() {
      if (!mondayIntegration) return;
      const res = await getIntegrationConfigData(mondayIntegration.id, undefined, { shouldExcludeRestrictedColumns });
      return res.boards.filter((board: any) => board.type === "board");
    }

    return (
      <>
        <div className={style.title}>
          Import board from <div className={style.mondayLogo}></div>
        </div>
        <div className={style.description}>
          Select a board from your monday.com account to import its data to your org chart.
        </div>

        <MondayBoardPicker
          onChooseItem={chooseBoard}
          fetchItems={fetchItems}
          placeholder="Search boards"
          actionButtonTitle="Import board data"
          primaryFilterField="name"
          secondaryFilterField={null}
        />
      </>
    );
  }

  function renderColumnMapper() {
    function chooseMapping(mapTo: keyof MandatoryColumnsMapping, colId: string) {
      const newMap = { ...selectedColumnMapping };
      for (const _key in newMap) {
        const key = _key as keyof typeof newMap;
        if (newMap[key] == colId) newMap[key] = null;
      }
      newMap[mapTo] = colId;
      setSelectedColumnMapping(newMap);
    }

    async function submit() {
      setSelectedIntegration({
        ...selectedIntegration,
        configuration: {
          ...selectedIntegration.configuration,
          columnMapping: selectedColumnMapping,
        },
      });
      updateBoardIntegration(selectedIntegration.id, {
        ...selectedIntegration.configuration,
        columnMapping: selectedColumnMapping,
      });
      setStep(OrgChartImportSteps.PickRoot);
    }

    function hasMandatoryFieldsMapped() {
      if (!shouldEnableDepartmentAsRoot) {
        return Object.values(selectedColumnMapping).every(Boolean);
      }

      const { departmentId, ...mandatoryMappingFields } = selectedColumnMapping;
      return Object.values(mandatoryMappingFields).every(Boolean);
    }

    return (
      <>
        <div className={style.title}>
          Import board from <div className={style.mondayLogo}></div>
        </div>
        <div className={style.description}>
          Map <strong>{selectedBoardName}</strong>&apos;s Columns to Org Chart
        </div>
        <div className={style.content}>
          <div className={style.dropdownGroup}>
            <DropdownSearch
              title="Employee Name"
              tooltip="Represent the title for individuals in the org chart"
              options={selectedIntegration.configuration.columns}
              isOptionValid={(col: { type: string }) => PossibleTypesForFields["Employee name"].includes(col.type)}
              selectedColId={selectedColumnMapping?.selfId}
              onChooseItem={chooseMapping}
              id="selfId"
              openDropdrownId={columnDropdownOpen}
              setOpenDropdownId={setColumnDropdownOpen}
            />

            <DropdownSearch
              title="Direct Manager"
              tooltip="Represent the person to whom the employee reports to. Content should match an entry in the Employee Name column."
              options={selectedIntegration.configuration.columns}
              isOptionValid={(col: { type: string }) => PossibleTypesForFields["Direct manager"].includes(col.type)}
              selectedColId={selectedColumnMapping?.parentId}
              onChooseItem={chooseMapping}
              id="parentId"
              openDropdrownId={columnDropdownOpen}
              setOpenDropdownId={setColumnDropdownOpen}
            />

            <DropdownSearch
              title="Position"
              tooltip="Describe the position or role of the employee."
              options={selectedIntegration.configuration.columns}
              isOptionValid={(col: { type: string }) => PossibleTypesForFields["Position"].includes(col.type)}
              selectedColId={selectedColumnMapping?.jobTitle}
              onChooseItem={chooseMapping}
              id="jobTitle"
              openDropdrownId={columnDropdownOpen}
              setOpenDropdownId={setColumnDropdownOpen}
            />

            {shouldEnableDepartmentAsRoot && (
              <DropdownSearch
                title="Department (optional)"
                tooltip="The department to which the employee is associated."
                options={selectedIntegration.configuration.columns}
                isOptionValid={(col: { type: string }) => PossibleTypesForFields["Department"].includes(col.type)}
                selectedColId={selectedColumnMapping?.departmentId || null}
                onChooseItem={chooseMapping}
                id="departmentId"
                openDropdrownId={columnDropdownOpen}
                setOpenDropdownId={setColumnDropdownOpen}
              />
            )}
          </div>
          <div className={style.actions}>
            <StyledButton
              title={"Continue"}
              onClick={submit}
              enabled={hasMandatoryFieldsMapped()}
              customStyle={{ width: "220px" }}
            />
          </div>
        </div>
      </>
    );
  }

  function renderRootPicker(shouldEnableDepartmentAsRoot: boolean) {
    async function chooseRootItem(item: any, allItems: any[]) {
      if (!mondayIntegration) {
        return;
      }

      selectedIntegration.configuration.columnMapping = selectedColumnMapping;
      const data = placeMindMapOrgChart(
        selectedIntegration.id,
        IntegrationType.monday,
        { ...selectedIntegration.configuration, itemId: item.id },
        { x: 0, y: 0 },
        allItems,
        selectedSourceType,
        shouldEnableDepartmentAsRoot,
        item.id
      );

      onDismiss && onDismiss(data);
    }

    function extractDepartments(data: any) {
      const { columns } = data[selectedIntegration.id];
      const departmentColumn: LiveIntegrationColumn = columns.find(
        (c: LiveIntegrationColumn) => c.id === selectedColumnMapping.departmentId
      );
      if (!departmentColumn) return [];

      const departmentsMap = departmentColumn.settings?.labels ?? {};
      const departments: DepartmentOption[] = [];

      for (const [key, title] of Object.entries(departmentsMap)) {
        const department = { id: key, columnId: departmentColumn.id, title: title as string };
        departments.push(department);
      }

      return departments;
    }

    async function fetchItems(query: string, abortController?: AbortController) {
      // TODO ofirc: the mapping is performed here and it's what the user picked
      // TODO yonatanha: limited to 100, when asking for more the integration fails
      const res = await getIntegrationItems(
        documentId,
        { [selectedIntegration.id]: { limit: 30, query: query } },
        { abortController, shouldExcludeRestrictedColumns }
      );

      if (shouldEnableDepartmentAsRoot && selectedSourceType === SourceNodeType.Department) {
        const departments = extractDepartments(res);
        return departments;
      }

      return res[selectedIntegration.id].items;
    }

    return (
      <>
        <div className={style.title}>
          {shouldEnableDepartmentAsRoot
            ? "Select source for the top of the chart"
            : "Select source name for the top of the chart"}
        </div>
        <div className={style.description}>
          {shouldEnableDepartmentAsRoot && selectedColumnMapping.departmentId ? (
            <span className={style.descriptionInstructions}>
              Select a person or department to set as the source of your org chart
            </span>
          ) : (
            <span>
              Select the person from the <strong>&quot;{selectedBoardName}&quot;</strong> board to appear on the top of
              your org chart
            </span>
          )}
        </div>

        {shouldEnableDepartmentAsRoot && selectedColumnMapping.departmentId ? (
          <div className={style.sourceTypeSelectionContainer}>
            <div className={style.sourceSelectionInnerContainer}>
              <span className={style.sourceSelectionText}>Choose source column</span>

              <DropdownPicker<SourceNodeType>
                options={Object.values(SourceNodeType)}
                isSelected={(sourceOption) => sourceOption === selectedSourceType}
                titles={(sourceOption) => sourceOption}
                onChange={(sourceOption) => setSelectedSourceType(sourceOption)}
                styles={{ optionClassName: style.sourceSelectionText }}
                optionRenderer={({ option }) => {
                  return { element: <span>{option}</span>, enabled: true };
                }}
                selectionHeaderRenderer={({ selectedOptions }) => {
                  const selectedRootOption = selectedOptions[0];
                  return <span className={style.sourceSelectionText}>{selectedRootOption}</span>;
                }}
                optionsContainerStyle={{
                  top: "100%",
                  boxShadow: "none",
                  border: "1px solid #dadce0",
                  borderBlockStart: "none",
                  padding: 0,
                }}
                enabled
              />
            </div>
          </div>
        ) : (
          ""
        )}
        <div className={style.content}>
          <Picker
            onChooseItem={chooseRootItem}
            fetchItems={fetchItems}
            placeholder="Type to search..."
            actionButtonTitle="Done"
            primaryFilterField={
              shouldEnableDepartmentAsRoot && selectedSourceType === SourceNodeType.Department ? "title" : "self"
            }
            secondaryFilterField={null}
            selectedSourceType={selectedSourceType}
          />
        </div>
      </>
    );
  }

  function renderStep(step: OrgChartImportSteps) {
    switch (step) {
      case OrgChartImportSteps.ChooseBoard: {
        return renderBoardPicker();
      }

      case OrgChartImportSteps.MapColumns: {
        return (
          <>
            <BackButton label="Back to boards" onClick={() => setStep(OrgChartImportSteps.ChooseBoard)} />
            {renderColumnMapper()}
          </>
        );
      }

      case OrgChartImportSteps.PickRoot: {
        return (
          <>
            <BackButton label="Back to columns" onClick={() => setStep(OrgChartImportSteps.MapColumns)} />
            {renderRootPicker(shouldEnableDepartmentAsRoot)}
          </>
        );
      }
    }
    return null;
  }

  return (
    <div className={style.container1}>
      <div
        className={classNames(style.closeIcon, style.hoverable)}
        onClick={() => onDismiss?.()}
        data-testid="close-template-modal"
      >
        <svg width="auto" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L16 16M16 1L1 16" stroke="#848199" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
      {renderStep(step)}
    </div>
  );
}

function BackButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div className={classNames(style.backButton)} onClick={onClick}>
      <Arrow direction="left" stroke={"#113357"} scale={0.8} />
      <span>{label}</span>
    </div>
  );
}

// don't we have this somewhere already??
enum LoadingState {
  notStarted,
  loading,
  success,
  error,
  actionButtonClicked,
}

const uniqById = R.uniqBy(R.prop("id"));

function Picker({
  onChooseItem,
  fetchItems,
  placeholder,
  actionButtonTitle,
  primaryFilterField,
  secondaryFilterField,
  selectedSourceType,
}: {
  onChooseItem: (item: any, allItems: any[]) => void;
  fetchItems: (query: string, abortController: AbortController) => any;
  placeholder: string;
  actionButtonTitle: string;
  primaryFilterField: string;
  secondaryFilterField: string | null;
  selectedSourceType: SourceNodeType;
}) {
  const [items, setItems] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 100);
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.loading);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const shouldEnableDepartmentAsRoot = useFeatureFlag("orgchart-department-enabled");

  function fetchSomeItems(searchTerm = "", controller = new AbortController()) {
    fetchItems(searchTerm, controller)
      .then((items: any[]) => {
        setItems((prev) => uniqById([...prev, ...items]));
        setLoadingState(LoadingState.success);
      })
      .catch((error: unknown) => {
        if (!(error instanceof CanceledError)) {
          setLoadingState(LoadingState.error);
        }
      });
  }

  useEffect(() => {
    if (!debouncedSearchTerm) {
      // on mount, we fetch the default items without search term. they should be part of items.
      // no need to refetch anything.
      // In fact, I would love to remember past queries and simply not repeat the search. perhaps
      // with useSWR with searchTerm as part of the key...
      return;
    }
    const controller = new AbortController();
    if (debouncedSearchTerm !== "") {
      setLoadingState(LoadingState.loading);
    }
    fetchSomeItems(debouncedSearchTerm, controller);
    return () => {
      setLoadingState((prev) => (prev === LoadingState.loading ? LoadingState.success : prev));
      controller.abort();
    };
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (!shouldEnableDepartmentAsRoot && selectedSourceType === SourceNodeType.Department) {
      return;
    }
    const controller = new AbortController();
    setSearchTerm("");
    setSelectedItemId(null);
    setItems([]);
    setLoadingState(LoadingState.loading);
    fetchSomeItems("", controller);
    return () => controller.abort();
  }, [selectedSourceType]);

  let itemsToShow = items;
  if (searchTerm) {
    const lowercase = searchTerm.toLocaleLowerCase();
    itemsToShow = items.filter(
      (item) =>
        (item[primaryFilterField] ? item[primaryFilterField].toLocaleLowerCase().includes(lowercase) : true) ||
        (secondaryFilterField && item[secondaryFilterField]?.toLocaleLowerCase().includes(lowercase))
    );
  }

  const buttonTitle =
    loadingState === LoadingState.notStarted
      ? "Loading..."
      : loadingState === LoadingState.loading
      ? "Loading..."
      : loadingState === LoadingState.success
      ? actionButtonTitle
      : loadingState === LoadingState.error
      ? "Error"
      : loadingState === LoadingState.actionButtonClicked
      ? "Working..."
      : "Error";

  function actionClicked() {
    if (!selectedItemId) {
      return;
    }
    const item = items.find((item) => item.id === selectedItemId);
    setLoadingState(LoadingState.actionButtonClicked);
    onChooseItem(item, items);
  }

  return (
    <div>
      <div className={style.pickerHeader}>
        <div className={style.searchContainer}>
          {
            <input
              type="text"
              value={searchTerm}
              placeholder={placeholder}
              autoFocus
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          }
        </div>
      </div>
      <ul className={style.pickerList}>
        {loadingState === LoadingState.error && <div className={style.pickerListItem}>Failed to load</div>}

        {itemsToShow
          .filter((item) => item[primaryFilterField])
          .map((item) => {
            const isSelected: boolean = selectedItemId === item.id;
            return (
              <li
                key={item.id}
                className={classNames(style.pickerListItem, { [style.checked]: isSelected })}
                onClick={() => setSelectedItemId(item.id)}
              >
                <div>
                  <div className={style.pickerListItemTitle}>{item[primaryFilterField]}</div>
                  {secondaryFilterField ? (
                    <div className={style.pickerListItemSubTitle}>{item[secondaryFilterField]}</div>
                  ) : (
                    <></>
                  )}
                </div>
                {isSelected && (
                  <span className={style.checkMark}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="11" viewBox="0 0 15 11" fill="none">
                      <path d="M2 5.5L5.66667 9L13 2" stroke="#1973FF" strokeWidth="1.5" strokeLinecap="square" />
                    </svg>
                  </span>
                )}
              </li>
            );
          })}
        {loadingState === LoadingState.loading && (
          <div className={style.shimmerContainer}>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
          </div>
        )}
      </ul>
      <div className={style.actions}>
        <StyledButton
          title={buttonTitle}
          onClick={actionClicked}
          enabled={!!selectedItemId && itemsToShow.some((item) => item.id == selectedItemId)}
          customStyle={{ width: "220px" }}
        />
      </div>
    </div>
  );
}

function DropdownSearch({
  title,
  tooltip,
  options,
  isOptionValid,
  selectedColId,
  onChooseItem,
  id,
  openDropdrownId,
  setOpenDropdownId,
}: {
  title: string;
  tooltip: string;
  options: any[];
  isOptionValid: (option: any) => boolean;
  selectedColId: string | null;
  onChooseItem: (item: any, mappingId: string) => void;
  id: string;
  openDropdrownId: string;
  setOpenDropdownId: React.Dispatch<React.SetStateAction<string>>;
}) {
  const handleOptionClick = (option: any) => {
    onChooseItem(id, option.id);
    setOpenDropdownId("");
  };

  return (
    <div className={style.dropdown}>
      <span className={style.withTooltip} data-tooltip={tooltip} style={{ gridArea: "icon" }}>
        <img
          src={getPathPrefix("/images/alert.svg")}
          alt="info"
          width="15px"
          height="15px"
          style={{
            filter: "invert(51%) sepia(8%) saturate(1416%) hue-rotate(171deg) brightness(92%) contrast(93%)",
            verticalAlign: "middle",
          }}
        />
      </span>

      <div className={style.description} style={{ gridArea: "label" }}>
        {title}
      </div>

      <span style={{ position: "relative", gridArea: "input" }}>
        <div
          role="button"
          className={style.columnPickerContainer}
          onClick={() => setOpenDropdownId((prev) => (prev == id ? "" : id))}
        >
          {options.find((p) => p.id == selectedColId)?.title || "Choose column"}
        </div>

        {openDropdrownId == id && (
          <div className={style.dropdownOptionsArea}>
            {options.map((option) => (
              <div
                key={option.id}
                className={classNames(style.pickerListItem, !isOptionValid(option) && style.disabled)}
                onClick={() => isOptionValid(option) && handleOptionClick(option)}
              >
                {option.title}
              </div>
            ))}
          </div>
        )}
      </span>
    </div>
  );
}

// TODO: we have 2 pickers: for boards and for root of orgchart. the UI is the same;
// we can merge them into one component
function MondayBoardPicker({
  onChooseItem,
  fetchItems,
  placeholder,
  actionButtonTitle,
  primaryFilterField,
  secondaryFilterField,
}: {
  onChooseItem: (item: any) => void;
  fetchItems: () => Promise<
    Array<{ id: string; name: string; board_kind: string; columns: { id: string; type: string }[] }>
  >;
  placeholder: string;
  actionButtonTitle: string;
  primaryFilterField: string;
  secondaryFilterField: string | null;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [incompatibleBoards, setIncompatibleBoards] = useState(new Set<string>());
  const [failedToLoad, setFailedToLoad] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [buttonTitle, setButtonTitle] = useState<string>("Loading");

  const debouncedSearchTerm = useDebounce(searchTerm, 100);
  const ref = useRef<any>(null);

  const filteredItems = useMemo(() => {
    if (!debouncedSearchTerm) {
      return items;
    }
    return items.filter(
      (item) =>
        item[primaryFilterField].toLowerCase().includes(debouncedSearchTerm) ||
        (secondaryFilterField && item[secondaryFilterField]?.toLowerCase().includes(debouncedSearchTerm))
    );
  }, [debouncedSearchTerm, items]);

  useEffect(() => {
    if (items.length) {
      return;
    }
    fetchItemsToPicker();
  }, []);

  useEffect(() => {
    if (selectedItem && selectedItem.id) {
      setSelectedItemId(selectedItem.id);
    } else {
      setSelectedItemId(null);
    }
  }, [selectedItem]);

  async function fetchItemsToPicker() {
    setIsLoading(true);
    setFailedToLoad(false);
    try {
      const fetchedItems = await fetchItems();
      // we separate boards that have minimum requirements from the rest
      // the ui will show them in different styles
      const [goodBoards, rejectedBoards] = R.partition(boardHasMinimumRequirements, fetchedItems);
      setItems(goodBoards.concat(rejectedBoards));
      setIncompatibleBoards(new Set(rejectedBoards.map((x) => x.id)));

      setButtonTitle(actionButtonTitle);
    } catch (e) {
      setFailedToLoad(true);
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }

  function actionClicked() {
    if (!selectedItemId) {
      return;
    }
    const item = items.find((item) => item.id === selectedItemId);
    setSelectedItemId(null);
    setButtonTitle("Loading...");
    setIsLoading(true);
    onChooseItem(item);
  }

  return (
    <div ref={ref} className={style.pickerContainer}>
      <div className={style.pickerHeader}>
        <div className={style.searchContainer}>
          {isLoading && <div className={style.shimmerLine} style={{ width: "50%" }}></div>}
          {!isLoading && (
            <input
              type="text"
              value={searchTerm}
              placeholder={placeholder}
              autoFocus
              onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
            />
          )}
        </div>
      </div>
      <ul className={style.pickerList}>
        {isLoading && (
          <div className={style.shimmerContainer}>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
            <div className={style.shimmerLine}></div>
          </div>
        )}
        {failedToLoad && <div className={style.pickerListItem}>Failed to load</div>}
        {!isLoading &&
          filteredItems.map((board) => {
            const isSelected = selectedItem?.id === board.id;
            const isDisabled = incompatibleBoards.has(board.id);
            return (
              <li
                key={board.id}
                className={classNames(style.boardListItem, {
                  [style.checked]: isSelected,
                  [style.disabled]: isDisabled,
                })}
                onClick={() => !isDisabled && setSelectedItem(board)}
              >
                <span style={{ stroke: "#848199" }}>
                  {board.board_kind === "private" ? privateBoardIcon(isDisabled) : publicBoardIcon(isDisabled)}
                </span>
                <span className={style.boardName}>{board.name}</span>
                <span className={style.boardKind}>{isDisabled ? "Not compatible" : board.board_kind}</span>
                <span>
                  {isSelected && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="11" viewBox="0 0 15 11" fill="none">
                      <path d="M2 5.5L5.66667 9L13 2" stroke="#1973FF" strokeWidth="1.5" strokeLinecap="square" />
                    </svg>
                  )}
                </span>
              </li>
            );
          })}
      </ul>
      <div className={style.actions}>
        {!!selectedItem && selectedItem?.board_kind == "private" && (
          <em>{"\u24D8 "}By importing a private board you are making it available for viewing on this canvas</em>
        )}
        <StyledButton
          title={buttonTitle}
          onClick={actionClicked}
          enabled={!!selectedItem}
          customStyle={{ width: "220px" }}
        />
      </div>
    </div>
  );
}

function publicBoardIcon(disabled: boolean) {
  const stroke = disabled ? "#848199" : "#113357";
  return (
    <svg
      style={{ flex: "0 0 auto", verticalAlign: "middle", height: "100%" }}
      width="17"
      height="14"
      viewBox="0 0 17 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M1 1L16.0625 1L16.0625 13.1765L1 13.1765L1 1Z" stroke={stroke} strokeWidth="1.2" />
      <path
        d="M5.20312 12.8562L5.20313 1.0001"
        stroke={stroke}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function privateBoardIcon(disabled: boolean) {
  const stroke = disabled ? "#848199" : "#113357";
  return (
    <svg
      style={{ flex: "0 0 auto", verticalAlign: "middle" }}
      width="20"
      height="15"
      viewBox="0 0 20 15"
      fill="none"
      stroke="red"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M16.0625 4L16.0625 1L12 1L1 1L1 13.1765L10 13.1765" stroke={stroke} strokeWidth="1.2" />
      <path
        d="M5.20312 12.8562L5.20313 1.0001"
        stroke={stroke}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="12" y="9.5" width="7.2" height="4.5" fill="#113357" stroke={stroke} strokeWidth="1.2" />
      <path
        d="M13.8008 8.78125V7.84105C13.8008 7.35277 13.9854 6.88449 14.3141 6.53923C14.6428 6.19397 15.0885 6 15.5533 6C16.0181 6 16.4639 6.19397 16.7925 6.53923C17.1212 6.88449 17.3059 7.35277 17.3059 7.84105V8.78125"
        stroke={stroke}
        strokeWidth="1.2"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </svg>
  );
}
